import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import { getAdminStats } from "@/utils/admin.functions";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DeletionRequestsTab } from "@/components/admin/DeletionRequestsTab";
import { Download, Users, TrendingUp, CalendarDays, Sparkles, ShieldAlert } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

export const Route = createFileRoute("/_app/admin")({
  component: AdminPage,
});

type Stats = Awaited<ReturnType<typeof getAdminStats>>;

function AdminPage() {
  const { user, loading } = useAuth();
  const { isAdmin, loading: adminLoading } = useIsAdmin();
  const navigate = useNavigate();
  const [stats, setStats] = useState<Stats | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [fetching, setFetching] = useState(true);

  useEffect(() => {
    if (loading || adminLoading) return;
    if (!user) { navigate({ to: "/auth" }); return; }
    if (!isAdmin) return;
    setFetching(true);
    getAdminStats()
      .then(setStats)
      .catch((e) => setError(e?.message ?? "Failed to load"))
      .finally(() => setFetching(false));
  }, [loading, adminLoading, user, isAdmin, navigate]);

  const csv = useMemo(() => {
    if (!stats) return "";
    const rows = [["Email", "Name", "Business Type", "Signed Up"]];
    for (const r of stats.waitlist) {
      rows.push([
        r.email,
        r.full_name ?? "",
        r.business_type ?? "",
        new Date(r.created_at).toISOString(),
      ]);
    }
    return rows
      .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(","))
      .join("\n");
  }, [stats]);

  function downloadCsv() {
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `waitlist-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  if (loading) return <div className="p-8 text-muted-foreground">Loading…</div>;
  if (!isAdmin) {
    return (
      <div className="p-8">
        <h1 className="text-2xl font-semibold mb-2">Forbidden</h1>
        <p className="text-muted-foreground">This page is restricted to administrators.</p>
      </div>
    );
  }
  if (fetching) return <div className="p-8 text-muted-foreground">Loading admin data…</div>;
  if (error) return <div className="p-8 text-destructive">{error}</div>;
  if (!stats) return null;

  return (
    <div className="p-6 md:p-8 space-y-8 max-w-7xl mx-auto">
      <div>
        <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
          <ShieldAlert className="w-8 h-8 text-orange-400" />
          <span className="bg-gradient-to-r from-orange-400 to-primary bg-clip-text text-transparent">Admin Dashboard</span>
        </h1>
        <p className="text-muted-foreground">Waitlist, subscriptions, and privacy requests</p>
      </div>

      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="deletions">Deletion requests</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-8">
          <section className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard icon={<Users className="w-4 h-4" />} label="Total signups" value={stats.totals.waitlistTotal} />
            <StatCard icon={<CalendarDays className="w-4 h-4" />} label="Today" value={stats.totals.today} />
            <StatCard icon={<TrendingUp className="w-4 h-4" />} label="This week" value={stats.totals.thisWeek} />
            <StatCard icon={<Sparkles className="w-4 h-4" />} label="This month" value={stats.totals.thisMonth} />
          </section>

          <Card>
            <CardHeader><CardTitle>Signups by business type</CardTitle></CardHeader>
            <CardContent className="h-72">
              {stats.byType.length === 0 ? (
                <p className="text-muted-foreground text-sm">No data yet.</p>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={stats.byType}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="type" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                    <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} allowDecimals={false} />
                    <Tooltip
                      contentStyle={{
                        background: "hsl(var(--popover))",
                        border: "1px solid hsl(var(--border))",
                        borderRadius: 8,
                      }}
                    />
                    <Bar dataKey="count" fill="hsl(var(--primary))" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>

          <section className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard label="Total users" value={stats.app.totalUsers} />
            <StatCard label="Active trials" value={stats.app.activeTrials} />
            <StatCard label="Paid subscribers" value={stats.app.paidSubscribers} />
            <StatCard label="Monthly revenue" value={`$${stats.app.monthlyRevenue.toLocaleString()}`} />
          </section>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Waitlist ({stats.waitlist.length})</CardTitle>
              <Button onClick={downloadCsv} size="sm" variant="outline">
                <Download className="w-4 h-4 mr-2" /> Export CSV
              </Button>
            </CardHeader>
            <CardContent className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Email</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Business type</TableHead>
                    <TableHead>Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {stats.waitlist.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell className="font-medium">{row.email}</TableCell>
                      <TableCell>{row.full_name}</TableCell>
                      <TableCell className="capitalize">{row.business_type}</TableCell>
                      <TableCell className="text-muted-foreground">
                        {new Date(row.created_at).toLocaleDateString()}
                      </TableCell>
                    </TableRow>
                  ))}
                  {stats.waitlist.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center text-muted-foreground py-8">
                        No signups yet.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="deletions">
          <DeletionRequestsTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function StatCard({
  icon, label, value,
}: { icon?: React.ReactNode; label: string; value: number | string }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center gap-2 text-muted-foreground text-xs mb-2">
          {icon}{label}
        </div>
        <div className="text-2xl font-semibold">{value}</div>
      </CardContent>
    </Card>
  );
}
